import { useEffect, useState } from 'react';
import {
  Search,
  X,
  Settings,
  Download,
  Bookmark,
  ChevronDown,
  Check,
  Trash2,
  Save,
  Plus,
  Users,
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { DashboardView } from './dashboardViews';
import { SavedView } from '@/components/operations/savedViews';
import { EntityConfig, EntityKey } from '@/components/entities/registry';

export interface ViewPick {
  entityKey: EntityKey;
  viewId: string;
}

export interface EntitySavedViews {
  entity: EntityConfig;
  views: SavedView[];
}

interface DashboardToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;

  dashboardViews: DashboardView[];
  activeDashboardViewId: string | null;
  currentUserId?: string;
  dirty: boolean;
  onSelectDashboard: (id: string | null) => void;
  onSaveDashboard: (name: string, isShared: boolean) => void;
  onUpdateDashboard: (id: string) => void;
  onDeleteDashboard: (id: string) => void;

  entityViews: EntitySavedViews[];
  onAddColumn: (pick: ViewPick) => void;
  onExport: () => void;
  layoutMode: boolean;
  onToggleLayout: () => void;
}

export function DashboardToolbar({
  search,
  onSearchChange,
  dashboardViews,
  activeDashboardViewId,
  currentUserId,
  dirty,
  onSelectDashboard,
  onSaveDashboard,
  onUpdateDashboard,
  onDeleteDashboard,
  entityViews,
  onAddColumn,
  onExport,
  layoutMode,
  onToggleLayout,
}: DashboardToolbarProps) {
  const activeView = dashboardViews.find((v) => v.id === activeDashboardViewId) ?? null;
  const displayName = activeView ? activeView.name : 'Default Dashboard';
  const isOwner = activeView ? activeView.ownerId === currentUserId : true;

  const myDashboards = dashboardViews.filter((v) => v.ownerId === currentUserId);
  const sharedDashboards = dashboardViews
    .filter((v) => v.ownerId !== currentUserId && v.isShared)
    .sort((a, b) => a.ownerEmail.localeCompare(b.ownerEmail) || a.name.localeCompare(b.name));

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShared, setNewShared] = useState(false);

  useEffect(() => {
    if (saveDialogOpen) {
      setNewName(activeView ? `${activeView.name} copy` : '');
      setNewShared(false);
    }
  }, [saveDialogOpen, activeView]);

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    onSaveDashboard(name, newShared);
    setSaveDialogOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <SearchBox value={search} onChange={onSearchChange} />

      <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white pr-1 h-9 w-[320px] shrink-0">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 pl-3 pr-2 h-full text-sm rounded-l-md hover:bg-slate-50 flex-1 min-w-0">
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold shrink-0">
                Dashboard:
              </span>
              <span className="flex items-center gap-1.5 flex-1 min-w-0">
                <span
                  className={cn(
                    'text-xs italic text-slate-400 shrink-0',
                    !dirty && 'invisible'
                  )}
                >
                  (modified)
                </span>
                <span className="font-semibold text-slate-900 truncate">{displayName}</span>
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-1">
            <div className="py-1 max-h-80 overflow-y-auto">
              <button
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-slate-50 text-left',
                  !activeDashboardViewId && 'bg-slate-50 font-medium'
                )}
                onClick={() => {
                  onSelectDashboard(null);
                  setPickerOpen(false);
                }}
              >
                {!activeDashboardViewId ? (
                  <Check className="w-4 h-4 text-slate-900" />
                ) : (
                  <span className="w-4 h-4" />
                )}
                <span className="text-slate-900">Default Dashboard</span>
              </button>

              {myDashboards.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400">
                    My Dashboards
                  </div>
                  {myDashboards.map((v) => (
                    <DashboardRow
                      key={v.id}
                      view={v}
                      active={activeDashboardViewId === v.id}
                      canDelete
                      onSelect={() => {
                        onSelectDashboard(v.id);
                        setPickerOpen(false);
                      }}
                      onDelete={() => onDeleteDashboard(v.id)}
                    />
                  ))}
                </>
              )}

              {sharedDashboards.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Shared
                  </div>
                  {sharedDashboards.map((v) => (
                    <DashboardRow
                      key={v.id}
                      view={v}
                      active={activeDashboardViewId === v.id}
                      canDelete={false}
                      showOwner
                      onSelect={() => {
                        onSelectDashboard(v.id);
                        setPickerOpen(false);
                      }}
                      onDelete={() => {}}
                    />
                  ))}
                </>
              )}

              {dashboardViews.length === 0 && (
                <div className="px-3 py-6 text-xs text-slate-400 text-center">
                  No saved dashboards yet
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <Popover
          open={saveMenuOpen}
          onOpenChange={(o) => dirty && setSaveMenuOpen(o)}
        >
          <PopoverTrigger asChild>
            <button
              disabled={!dirty}
              className={cn(
                'flex items-center justify-center w-7 h-7 rounded-md shrink-0 transition-opacity',
                dirty
                  ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  : 'opacity-0 pointer-events-none'
              )}
              title="Save dashboard"
            >
              <Save className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-1">
            {activeView && isOwner && (
              <button
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm text-left"
                onClick={() => {
                  onUpdateDashboard(activeView.id);
                  setSaveMenuOpen(false);
                }}
              >
                <Save className="w-4 h-4 text-slate-500" />
                Save changes
              </button>
            )}
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm text-left"
              onClick={() => {
                setSaveMenuOpen(false);
                setSaveDialogOpen(true);
              }}
            >
              <Save className="w-4 h-4 text-slate-500" />
              Save as new dashboard…
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onToggleLayout}
          className={
            layoutMode
              ? 'flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium bg-slate-900 text-white'
              : 'flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }
          title={layoutMode ? 'Exit layout mode' : 'Edit dashboard layout'}
        >
          {layoutMode ? 'Done' : 'Layout'}
        </button>
        <SettingsMenu entityViews={entityViews} onAddColumn={onAddColumn} />
        <button
          onClick={onExport}
          className="flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
          title="Export dashboard"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">
                Dashboard name
              </label>
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                }}
                placeholder="My dashboard"
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox
                checked={newShared}
                onCheckedChange={(v) => setNewShared(!!v)}
              />
              <span className="flex items-center gap-1.5 text-sm text-slate-900">
                <Users className="w-3.5 h-3.5 text-slate-500" />
                Share with everyone
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newName.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface DashboardRowProps {
  view: DashboardView;
  active: boolean;
  canDelete: boolean;
  showOwner?: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function DashboardRow({ view, active, canDelete, showOwner, onSelect, onDelete }: DashboardRowProps) {
  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm hover:bg-slate-50',
        active && 'bg-slate-50 font-medium'
      )}
    >
      <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={onSelect}>
        {active ? (
          <Check className="w-4 h-4 text-slate-900 shrink-0" />
        ) : (
          <span className="w-4 h-4 shrink-0" />
        )}
        <Bookmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="flex flex-col min-w-0 leading-tight">
          <span className="text-slate-900 truncate">{view.name}</span>
          {showOwner && (
            <span className="text-[11px] italic text-slate-400 truncate">
              {view.ownerEmail || 'unknown'}
            </span>
          )}
        </span>
      </button>
      {canDelete && (
        <button
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
          onClick={onDelete}
          title="Delete dashboard"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
}

function SearchBox({ value, onChange }: SearchBoxProps) {
  return (
    <div className="flex-1 min-w-0 max-w-xs flex items-center gap-2 border border-slate-200 bg-white rounded-md h-9 pl-3 pr-1">
      <Search className="w-4 h-4 text-slate-400 shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search cards"
        className="flex-1 min-w-0 text-sm outline-none bg-transparent"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="text-slate-400 hover:text-slate-600 p-1 shrink-0"
          title="Clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

interface SettingsMenuProps {
  entityViews: EntitySavedViews[];
  onAddColumn: (pick: ViewPick) => void;
}

function SettingsMenu({ entityViews, onAddColumn }: SettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<'root' | 'add-column'>('root');
  const anyViews = entityViews.some((e) => e.views.length > 0);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSubmenu('root');
      }}
    >
      <PopoverTrigger asChild>
        <button
          className="flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
          title="Dashboard settings"
        >
          <Settings className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1">
        {submenu === 'root' ? (
          <div className="py-1">
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm"
              onClick={() => setSubmenu('add-column')}
            >
              <Plus className="w-4 h-4 text-slate-500" />
              Add column
            </button>
          </div>
        ) : (
          <div className="py-1 max-h-80 overflow-y-auto">
            {!anyViews && (
              <div className="px-3 py-6 text-xs text-slate-400 text-center">
                Save a view in any tab first
              </div>
            )}
            {entityViews.map(({ entity, views }) =>
              views.length === 0 ? null : (
                <div key={entity.key} className="mb-1">
                  <div className="px-3 pt-1 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400">
                    {entity.label}
                  </div>
                  {views.map((v) => (
                    <button
                      key={v.id}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm hover:bg-slate-50 text-left"
                      onClick={() => {
                        onAddColumn({ entityKey: entity.key, viewId: v.id });
                        setOpen(false);
                      }}
                    >
                      <Bookmark className="w-3.5 h-3.5 text-slate-400" />
                      <span className="flex flex-col min-w-0 leading-tight">
                        <span className="text-slate-900 truncate">{v.name}</span>
                        {v.isShared && v.ownerEmail && (
                          <span className="text-[11px] italic text-slate-400 truncate">
                            {v.ownerEmail}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
